// @ts-strict-ignore
import * as React from 'react';
import type { RootState } from '../reducers/reducers';
import { useDispatch, useSelector } from 'react-redux';
import {
    AppPlatform,
    BuildStatus,
    ReleaseCandidateBuildInfo,
    CIBuildSelectionReason,
    ReleaseStepReleaseCandidateData,
    BuildInfo,
    AutomationType,
    RunwayResponseType,
} from '../types';
import { getIsLoading, getUpdated, getError, showArtifactsModuleEvenIfEmpty, getAutomationIdByType } from '../helpers';
import styles from './styles/stepStyles';
import { FeatureSection, StepRunComponentProps, type StringKeyedDictionary } from '../typescriptHelpers';
import { dayjs } from '@/extendedDayjs';
import Text from 'antd/lib/typography/Text';
import Layout, { Content, Footer } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import {
    secondaryTextForBuildStatus,
    statusColorForBuildStatus,
    displayCIText,
    buildStatusStringForBuild as buildStatusStringForBuildHelper,
} from '../helpers/displayHelpers';
import WorkflowBuildModule from './workflowModule/WorkflowBuildModule';
import List from 'antd/lib/list';
import Collapse from 'antd/lib/collapse';
import { antGrey, runwayColors } from './styles/globalStyles';
import { LockOutlined, RedoOutlined } from '@ant-design/icons';
import Link from 'antd/lib/typography/Link';
import { getLocaleDateTimeFormat } from '../timeHelpers';
import CommitInfoModule from './workflowModule/CommitInfoModule';
import ArtifactsModule from './workflowModule/ArtifactsModule';
import SmallStatusNode from './SmallStatusNode';
import { type BaseUpdateReleaseRequest, type SelectRCBuildRequest, tryUpdateRelease } from '../actions/release';
import { ReleaseUpdatable } from '../actions/updatable';
import { forIn } from 'lodash';
import { ReleaseStepInactiveContext } from '../contexts';
import Divider from 'antd/lib/divider';
import { AppBuildComponent } from './BuildComponents';
import { RunwayUserAction } from '../helpers/aclHelpers';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import {
    selectRelease,
    selectAppIdentifiers,
    selectStabilityIntegrations,
    selectReleaseStep,
    selectAutomationById,
    selectAppDistributionServices,
    selectIsWorkflowsCIStepEnabled,
    selectStepRun,
    selectAppTimeFormatSetting,
    selectAppPlatform,
} from '../selectors/selectors';
import { ReleaseEventsTimelineSection } from './releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from './timelineEvents/EventsTimeline';
import { globalMessage } from '../utils/globalMessage';
import { EmptyStateWrapper } from './releaseEmptyState/EmptyStateWrapper';
import type { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { useCallUpdatable, useCallUpdatableForCollections } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';

export const StepContentReleaseCandidate = ({
    orgId,
    appId,
    releaseId,
    releaseStepType,
    stepRunComponentProps,
}: ReleaseStepOrStepRunComponentProps) => {
    const dispatch = useDispatch();
    const isStepInactive = React.useContext(ReleaseStepInactiveContext);
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));

    const releaseStep = useSelector((state: RootState) => selectReleaseStep(state, releaseId, releaseStepType));
    const stepRunId = stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));

    const stepStatus = stepRun ? stepRun.status : releaseStep.status;
    const automationRuns = stepRun ? stepRun.automationRuns : releaseStep.automationRuns;
    const stepData: ReleaseStepReleaseCandidateData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;

    const builds = [...(stepData.builds ?? [])].sort((a, b) => {
        const aNum = parseInt(a.ciBuild?.buildIdentifier ?? '', 10);
        const bNum = parseInt(b.ciBuild?.buildIdentifier ?? '', 10);
        if (!isNaN(aNum) && !isNaN(bNum)) {
            return bNum - aNum;
        }
        const aTime = a.ciBuild?.buildStartedAt ?? '';
        const bTime = b.ciBuild?.buildStartedAt ?? '';
        return bTime.localeCompare(aTime);
    });
    const selectedBuild = builds.find((build) => build.isSelectedBuild);
    const ciBuild = selectedBuild?.ciBuild;
    const buildDuration =
        ciBuild?.buildStartedAt &&
        ciBuild?.buildFinishedAt &&
        dayjs.duration(dayjs(ciBuild?.buildFinishedAt).diff(dayjs(ciBuild?.buildStartedAt))).humanize();
    const workflowData = stepData.workflowData;
    const workflowBranch = workflowData?.branch;

    // In the context of CI/RC step, we only use platform to identify whether the app is OTA/SDK and
    // to show a tooltip with more detailed information about the CI workflow.
    // For workflow-migrated steps, we cannot always assume there'll be a single platform since
    // the step can point to multiple destinations with different platforms (e.g. iOS and Android).
    // By setting platform as null here, we'll just show a default tooltip, which is fine.
    const platform = useSelector((state: RootState) => {
        return stepRun ? null : selectAppPlatform(state, appId);
    });

    const buildStates = useSelector((state: RootState) => {
        const result: {
            loading: StringKeyedDictionary<boolean>;
            success: StringKeyedDictionary<boolean>;
            error: StringKeyedDictionary<string>;
        } = { loading: {}, success: {}, error: {} };

        builds.forEach((build) => {
            const buildHash = build.ciBuild.buildHash;
            result.loading[buildHash] = getIsLoading(ReleaseUpdatable.SelectReleaseCandidateBuild, state, buildHash);
            result.success[buildHash] = getUpdated(ReleaseUpdatable.SelectReleaseCandidateBuild, state, buildHash);
            result.error[buildHash] = getError(ReleaseUpdatable.SelectReleaseCandidateBuild, state, buildHash);
        });

        return result;
    });

    const [lockBuild, lockBuildIsLoadingMap, lockBuildResponseTypeMap, lockBuildErrorMap] =
        useCallUpdatableForCollections(
            StepRunUpdatable.LockBuild,
            builds.reduce<Record<string, StepRunComponentProps>>((acc, item) => {
                acc[item.ciBuild?.buildHash] = stepRunComponentProps;
                return acc;
            }, {}),
            builds.map((b) => b.ciBuild?.buildHash),
        );

    const selectRCBuildIsLoadingMap = stepRun ? lockBuildIsLoadingMap : buildStates.loading;
    const selectRCBuildErrorMap = stepRun ? lockBuildErrorMap : buildStates.error;
    const lockBuildResponseTypeBooleanMap = Object.entries(lockBuildResponseTypeMap).reduce<Record<string, boolean>>(
        (acc, [key, value]) => {
            acc[key] = value === RunwayResponseType.Success;
            return acc;
        },
        {},
    );
    const selectRCBuildSuccessMap = stepRun ? lockBuildResponseTypeBooleanMap : buildStates.success;

    const [unlockBuild, unlockBuildIsLoading, unlockBuildResponseType, unlockBuildError] = useCallUpdatable(
        StepRunUpdatable.UnlockBuild,
        stepRunComponentProps,
    );

    const [overrideCiFailure, overrideCiFailureIsLoading, overrideCiFailureResponseType, overrideCiFailureError] =
        useCallUpdatable(StepRunUpdatable.OverrideCIFailure, stepRunComponentProps);

    const userId = useSelector((state: RootState) => state.user.id);

    const resumeAutoSelectingLatestIsLoading = useSelector((state: RootState) =>
        stepRun ? unlockBuildIsLoading : (
            getIsLoading(ReleaseUpdatable.ResumeAutoSelectingLatestReleaseCandidateBuild, state)
        ),
    );
    const resumeAutoSelectingLatestUpdated = useSelector((state: RootState) =>
        stepRun ?
            unlockBuildResponseType === RunwayResponseType.Success
        :   getUpdated(ReleaseUpdatable.ResumeAutoSelectingLatestReleaseCandidateBuild, state),
    );
    const resumeAutoSelectingLatestError = useSelector((state: RootState) =>
        stepRun ? unlockBuildError : getError(ReleaseUpdatable.ResumeAutoSelectingLatestReleaseCandidateBuild, state),
    );

    const distributionServices = useSelector((state: RootState) => selectAppDistributionServices(state, appId));
    const stabilityIntegrations = useSelector((state: RootState) => selectStabilityIntegrations(state, appId));
    const appIdentifiers = useSelector((state: RootState) => selectAppIdentifiers(state, appId));
    const version = useSelector((state: RootState) => selectRelease(state, releaseId).version);

    const autoUploadDsymsToStabilityMonitoringAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoUploadDsymsToStabilityMonitoring,
    );

    const autoUploadBinaryArtifactToAppStoreAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoUploadBinaryArtifactToAppStore,
    );

    const autoDownloadArtifactsAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoDownloadArtifacts,
    );

    const autoUploadDsymsToStabilityMonitoring = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoUploadDsymsToStabilityMonitoringAutomationId),
    );

    const autoUploadBinaryArtifactToAppStore = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoUploadBinaryArtifactToAppStoreAutomationId),
    );
    const autoDownloadArtifacts = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoDownloadArtifactsAutomationId),
    );

    const artifactAutomations = {
        [AutomationType.AutoUploadDsymsToStabilityMonitoring]: autoUploadDsymsToStabilityMonitoring,
        [AutomationType.AutoUploadBinaryArtifactToAppStore]: autoUploadBinaryArtifactToAppStore,
        [AutomationType.AutoDownloadArtifacts]: autoDownloadArtifacts,
    };

    const showArtifactsModule = showArtifactsModuleEvenIfEmpty(artifactAutomations);
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));

    const selectRCBuild = React.useCallback(
        (buildHash: string) => {
            if (stepRun) {
                return lockBuild[buildHash]({ buildHash });
            }

            const request: SelectRCBuildRequest = {
                orgId,
                appId,
                releaseId,
                buildHash,
            };

            dispatch(tryUpdateRelease(ReleaseUpdatable.SelectReleaseCandidateBuild, request, buildHash));
        },
        [dispatch, orgId, appId, releaseId, stepRun, lockBuild],
    );

    const resumeAutoSelectingLatest = React.useCallback(() => {
        if (stepRun) {
            return unlockBuild();
        }

        const request: BaseUpdateReleaseRequest = {
            orgId: orgId,
            appId: appId,
            releaseId: releaseId,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.ResumeAutoSelectingLatestReleaseCandidateBuild, request));
    }, [dispatch, orgId, appId, releaseId, stepRun, unlockBuild]);

    const renderItem = (buildInfo: ReleaseCandidateBuildInfo, index: number) => {
        const build = buildInfo.ciBuild;
        const tagColor = statusColorForBuildStatus(build.buildStatus);
        const finishedAtMoment = build.buildFinishedAt ? dayjs(build.buildFinishedAt) : null;
        const isBuildNotInProgress = build.buildStatus !== BuildStatus.InProgress;
        const selectedRCBuildIsLoading = selectRCBuildIsLoadingMap[build.buildHash];
        let activeBuildText = null;

        if (buildInfo.isSelectedBuild) {
            switch (buildInfo.selectedReason) {
                case CIBuildSelectionReason.AutoSelection:
                    activeBuildText = 'Active (latest build)';
                    break;
                case CIBuildSelectionReason.ManualSelection:
                    activeBuildText = 'Active (manual selection)';
                    break;
            }
        }

        const showSelectAsActiveButton =
            !buildInfo.isSelectedBuild ||
            (buildInfo.isSelectedBuild && buildInfo.selectedReason === CIBuildSelectionReason.AutoSelection);

        return (
            <List.Item key={index}>
                <Collapse ghost bordered={false}>
                    <Collapse.Panel
                        className="collapse-panel-rc-builds"
                        key={build.buildIdentifier}
                        header={
                            <Space align="center" size={12}>
                                <SmallStatusNode color={tagColor} size={10} />
                                <Space split={<Divider type="vertical"></Divider>} align="center">
                                    <Space align="center" style={{ minWidth: 0 }}>
                                        {build.buildIdentifier &&
                                            (build.buildUrl ?
                                                <Link href={build.buildUrl} target="_blank" strong ellipsis>
                                                    Build #{build.buildIdentifier}
                                                </Link>
                                            :   <Text style={{ color: antGrey.grey8 }} strong ellipsis>
                                                    Build #{build.buildIdentifier}
                                                </Text>)}
                                        {finishedAtMoment && (
                                            <Text type="secondary" ellipsis>
                                                - finished{' '}
                                                {finishedAtMoment.format(
                                                    getLocaleDateTimeFormat('short', use24HourTimeFormat),
                                                )}
                                            </Text>
                                        )}
                                    </Space>
                                    {buildInfo.associatedAppStoreBuild && (
                                        <AppBuildComponent
                                            integrationId={buildInfo.associatedAppStoreBuild.integrationId}
                                            buildIdentifier={buildInfo.associatedAppStoreBuild.buildNumber}
                                            isProcessed={buildInfo.associatedAppStoreBuild.isProcessed}
                                        />
                                    )}
                                </Space>
                            </Space>
                        }
                        extra={
                            <Space align="center" style={{ minWidth: 0 }}>
                                {buildInfo.isSelectedBuild && (
                                    <Text style={{ color: runwayColors.statusGreen, fontWeight: 600 }} ellipsis>
                                        {platform === AppPlatform.ReactNativeOTA ? 'Latest' : activeBuildText}
                                    </Text>
                                )}
                                {platform !== AppPlatform.ReactNativeOTA && showSelectAsActiveButton && (
                                    <PermissionedActionButton
                                        actionType={RunwayUserAction.UAReleaseAppUpdate} // close enough
                                        releaseId={releaseId}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            selectRCBuild(build.buildHash);
                                        }}
                                        disabled={!isBuildNotInProgress || isStepInactive}
                                        loading={selectedRCBuildIsLoading}
                                        icon={<LockOutlined style={{ color: antGrey.grey8 }} />}
                                    >
                                        Lock as active RC
                                    </PermissionedActionButton>
                                )}
                            </Space>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <CommitInfoModule
                                commitLabel={'Commit'}
                                commit={build.commit}
                                commitAuthor={build.commitAuthor}
                                commitMessage={build.commitMessage}
                                branch={workflowBranch}
                            />
                            {showArtifactsModule && (
                                <ArtifactsModule
                                    appId={appId}
                                    orgId={orgId}
                                    releaseId={releaseId}
                                    version={version}
                                    artifacts={build.artifacts ?? []}
                                    services={distributionServices}
                                    stabilityIntegrations={stabilityIntegrations}
                                    appIdentifiers={appIdentifiers}
                                    buildStatus={build.buildStatus}
                                    canRetry={true}
                                    showHeader={true}
                                    isPublic={false}
                                    stepRunComponentProps={stepRunComponentProps}
                                />
                            )}
                        </div>
                    </Collapse.Panel>
                </Collapse>
            </List.Item>
        );
    };

    const buildsListHeader = () => {
        const isManuallySelectedBuild = selectedBuild?.selectedReason === CIBuildSelectionReason.ManualSelection;

        return (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 20, color: antGrey.grey7 }}>
                    Build history – {builds.length} {builds.length === 1 ? 'total build' : 'total builds'}
                </Text>
                {isManuallySelectedBuild && (
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UASetResumeActiveRCBuild}
                        releaseId={releaseId}
                        onClick={resumeAutoSelectingLatest}
                        icon={<RedoOutlined style={{ color: runwayColors.info }} />}
                        loading={resumeAutoSelectingLatestIsLoading}
                    >
                        Resume auto-selecting latest
                    </PermissionedActionButton>
                )}
            </div>
        );
    };

    const buildStatusStringForBuild = (
        isReleaseFromScraper: boolean,
        buildInfo?: BuildInfo,
        isOverridden?: boolean,
    ) => {
        if (isReleaseFromScraper) {
            return 'Success';
        }
        if (isOverridden && buildInfo?.buildStatus === BuildStatus.Failure) {
            return 'Approved (override)';
        }
        return buildStatusStringForBuildHelper(buildInfo);
    };

    const buildStatusColorForBuildStatus = (isReleaseFromScraper: boolean, buildStatus?: BuildStatus) => {
        if (isReleaseFromScraper) {
            return runwayColors.statusGrey;
        }

        return statusColorForBuildStatus(buildStatus);
    };

    const secondaryText = (
        isReleaseFromScraper: boolean,
        buildStatus?: BuildStatus,
        buildDuration?: string,
        buildIdentifier?: string,
        isOverridden?: boolean,
    ) => {
        if (isReleaseFromScraper) {
            return `Your ${displayCIText(isWorkflowsCIStepEnabled, 'lowercase')} build has succeeded.`;
        }
        if (isOverridden && buildStatus === BuildStatus.Failure) {
            return 'The workflow run failed but build was manually approved.';
        }
        return secondaryTextForBuildStatus(buildStatus, buildDuration, buildIdentifier);
    };

    React.useEffect(() => {
        forIn(selectRCBuildErrorMap, (error) => {
            if (error) {
                globalMessage.error(error);
            }
        });
    }, [selectRCBuildErrorMap]);

    React.useEffect(() => {
        forIn(selectRCBuildSuccessMap, (isUpdated) => {
            if (isUpdated) {
                globalMessage.success('Selected RC build updated!');
            }
        });
    }, [selectRCBuildSuccessMap]);

    React.useEffect(() => {
        if (resumeAutoSelectingLatestError) {
            globalMessage.error(resumeAutoSelectingLatestError);
        }
    }, [resumeAutoSelectingLatestError]);

    React.useEffect(() => {
        if (resumeAutoSelectingLatestUpdated) {
            globalMessage.success('Runway will now resume automatically selecting the latest build');
        }
    }, [resumeAutoSelectingLatestUpdated]);

    React.useEffect(() => {
        if (overrideCiFailureError) {
            globalMessage.error(overrideCiFailureError);
        }
    }, [overrideCiFailureError]);

    React.useEffect(() => {
        if (overrideCiFailureResponseType === RunwayResponseType.Success) {
            globalMessage.success('CI failure override updated.');
        }
    }, [overrideCiFailureResponseType]);

    const ciBuildFailed = selectedBuild?.ciBuild?.buildStatus === BuildStatus.Failure;
    const showOverrideButton = !!stepRunComponentProps && !!userId && ciBuildFailed;
    const isOverridden = !!stepData.ciFailureApproved;
    const onClickOverrideCiFailure = React.useCallback(() => {
        if (!userId) return;
        overrideCiFailure({ isApproved: !isOverridden, userId });
    }, [userId, isOverridden, overrideCiFailure]);

    return (
        <Layout>
            <Content
                style={{
                    ...styles.innerContent,
                    ...(showOverrideButton ? styles.contentWithFooterStyle : {}),
                    paddingTop: 0,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'stretch',
                        marginBottom: 24,
                    }}
                >
                    <div style={{ display: 'flex', flexShrink: 0, paddingTop: 28 }}>
                        <EmptyStateWrapper
                            releaseStepType={releaseStepType}
                            appId={appId}
                            releaseId={releaseId}
                            featureSections={[]}
                            shouldForceDisable
                        >
                            {(isReleaseFromScraper) => {
                                return (
                                    <Space direction="vertical" size={4}>
                                        <Text
                                            style={{
                                                ...styles.stepContentStatusText,
                                                color:
                                                    isOverridden && ciBuildFailed ?
                                                        runwayColors.statusGreen
                                                    :   buildStatusColorForBuildStatus(
                                                            isReleaseFromScraper,
                                                            selectedBuild?.ciBuild?.buildStatus,
                                                        ),
                                            }}
                                        >
                                            {buildStatusStringForBuild(
                                                isReleaseFromScraper,
                                                selectedBuild?.ciBuild,
                                                isOverridden,
                                            )}
                                        </Text>
                                        <Text type="secondary">
                                            {secondaryText(
                                                isReleaseFromScraper,
                                                selectedBuild?.ciBuild.buildStatus,
                                                buildDuration,
                                                selectedBuild?.ciBuild.buildIdentifier,
                                                isOverridden,
                                            )}
                                        </Text>
                                    </Space>
                                );
                            }}
                        </EmptyStateWrapper>
                    </div>
                    <ReleaseEventsTimelineSection
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        stepRunId={stepRunComponentProps?.stepRunId}
                        context={ReleaseTimelineSectionContext.ReleaseStep}
                        releaseStepType={releaseStepType}
                    />
                </div>
                <EmptyStateWrapper
                    releaseStepType={releaseStepType}
                    appId={appId}
                    releaseId={releaseId}
                    featureSections={[FeatureSection.ReleaseCandidateActiveBuild]}
                    shouldCenterContent
                >
                    <div>
                        <WorkflowBuildModule
                            orgId={orgId}
                            moduleTitle={selectedBuild ? 'Active build' : null}
                            appId={appId}
                            releaseId={releaseId}
                            releaseStepType={releaseStepType}
                            releaseStepStatus={stepStatus}
                            platform={platform}
                            workflowData={workflowData}
                            isWorkflowTriggerAllowedForCompletedRelease={
                                stepData.isWorkflowTriggerAllowedForCompletedRelease
                            }
                            buildInfo={selectedBuild}
                            autoUploadDsymsToStabilityMonitoringAutomationId={
                                autoUploadDsymsToStabilityMonitoringAutomationId
                            }
                            autoUploadBinaryArtifactToAppStoreAutomationId={
                                autoUploadBinaryArtifactToAppStoreAutomationId
                            }
                            autoDownloadArtifactsAutomationId={autoDownloadArtifactsAutomationId}
                            stepRunComponentProps={stepRunComponentProps}
                        />
                    </div>
                </EmptyStateWrapper>
                <EmptyStateWrapper
                    releaseStepType={releaseStepType}
                    appId={appId}
                    releaseId={releaseId}
                    featureSections={[FeatureSection.ReleaseCandidateBuildHistory]}
                    shouldCenterContent
                >
                    <div>
                        <List
                            className="build-history-list"
                            header={buildsListHeader()}
                            locale={{ emptyText: 'No builds found.' }}
                            dataSource={builds}
                            itemLayout="vertical"
                            pagination={{ defaultPageSize: 10, hideOnSinglePage: true }}
                            renderItem={renderItem}
                            size="small"
                            style={builds.length === 0 ? { marginTop: 24 } : {}}
                        ></List>
                    </div>
                </EmptyStateWrapper>
            </Content>
            {showOverrideButton && (
                <Footer style={styles.footerStyle}>
                    <Space>
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAReleaseAppUpdate}
                            releaseId={releaseId}
                            loading={overrideCiFailureIsLoading}
                            onClick={onClickOverrideCiFailure}
                        >
                            {isOverridden ? 'Rescind approval of this step' : 'Override status and approve this step'}
                        </PermissionedActionButton>
                    </Space>
                </Footer>
            )}
        </Layout>
    );
};
